import { useCallback, useEffect, useRef, useState } from 'react';
import { audioApi } from '@/api/audio.api';

/**
 * Extrait le nom du blob depuis une URL de stockage deja persistee en base.
 *
 * Les API renvoient aujourd'hui des URL completes (Azure, avec ou sans jeton
 * signe). L'endpoint prive attend un nom de blob, pas une URL : d'ou cette
 * conversion cote client. Les URL de session locale (MediaRecorder) sont
 * rejetees : elles ne correspondent a aucun objet stocke.
 */
export function blobNameFromAudioUrl(url: string | null | undefined): string | null {
  if (typeof url !== 'string') return null;
  const trimmed = url.trim();
  if (!trimmed) return null;
  // blob: ou data: -> audio local, rien a re-telecharger.
  if (/^(blob:|data:)/i.test(trimmed)) return null;

  // On retire la query string (jeton signe eventuel) avant de prendre le segment.
  const withoutQuery = trimmed.split(/[?#]/)[0];
  const lastSegment = withoutQuery.split('/').filter(Boolean).pop();
  if (!lastSegment) return null;

  try {
    return decodeURIComponent(lastSegment);
  } catch {
    // Pourcentage mal forme : on rend la forme brute plutot que d echouer.
    return lastSegment;
  }
}

type PrivateAudioState = {
  url: string | null;
  isLoading: boolean;
  error: unknown;
};

/**
 * Charge un audio par l'endpoint prive et renvoie une URL d'objet utilisable
 * par `<audio src>` ou `new Audio(url)`.
 *
 * Points de vigilance :
 * - l'URL d'objet est revoquee au demontage ET a chaque changement de blob,
 *   sinon chaque navigation fuit un blob en memoire ;
 * - un changement de blob pendant un chargement en cours ne doit pas_ecrire
 *   dans un composant demonte ;
 * - un 403 (acces refuse par la regle metier) n'est pas une erreur de session :
 *   le client ne doit pas se deconnecter, l'intercepteur 401 s'en charge deja.
 */
export function usePrivateAudioUrl(
  audioUrl: string | null | undefined,
  options: { enabled?: boolean } = {},
): PrivateAudioState & { load: () => void } {
  const enabled = options.enabled ?? true;
  const blobName = blobNameFromAudioUrl(audioUrl);

  const [state, setState] = useState<PrivateAudioState>({
    url: null,
    isLoading: false,
    error: null,
  });

  const objectUrlRef = useRef<string | null>(null);
  const requestIdRef = useRef(0);
  const mountedRef = useRef(true);

  const release = useCallback(() => {
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      release();
    };
  }, [release]);

  useEffect(() => {
    // Invalidation de toute requete precedente avant de demarrer la suivante.
    requestIdRef.current += 1;
    const requestId = requestIdRef.current;

    release();
    setState({ url: null, isLoading: false, error: null });

    if (!enabled || !blobName) return;

    setState({ url: null, isLoading: true, error: null });

    audioApi
      .getPrivateAudioContent(blobName)
      .then((blob) => {
        if (!mountedRef.current || requestId !== requestIdRef.current) return;
        const objectUrl = URL.createObjectURL(blob);
        objectUrlRef.current = objectUrl;
        setState({ url: objectUrl, isLoading: false, error: null });
      })
      .catch((error: unknown) => {
        if (!mountedRef.current || requestId !== requestIdRef.current) return;
        setState({ url: null, isLoading: false, error });
      });
  }, [blobName, enabled, release]);

  const load = useCallback(() => {
    // Relance manuelle : incrémente l'identifiant pour forcer le rafraichissement.
    requestIdRef.current += 1;
  }, []);

  return { ...state, load };
}

/**
 * Variante imperative pour les ecrans qui declenchent la lecture au clic
 * (`new Audio(url)`) : le blob n'est telecharge qu'a la demande, pas au
 * montage de la page.
 *
 * L'URL d'objet est revoquee a la fin de la lecture (`ended`), en cas
 * d'erreur, a l'arret, et au demontage. Un garde-fou temporel couvre le cas
 * ou aucun evenement ne survient jamais.
 *
 * Une seule lecture court a la fois : une nouvelle demande arrete la
 * precedente, et l'admin qui reclique sur le meme enregistrement l'arrete.
 */
export function usePrivateAudioPlayer() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [playingBlobName, setPlayingBlobName] = useState<string | null>(null);
  const mountedRef = useRef(true);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const objectUrlRef = useRef<string | null>(null);
  const failsafeRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /**
   * Arrete la lecture en cours et libere son URL d'objet. Appele a chaque
   * nouvelle demande, a l'arret, et au demontage.
   */
  const stop = useCallback(() => {
    if (failsafeRef.current !== null) {
      clearTimeout(failsafeRef.current);
      failsafeRef.current = null;
    }
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.removeAttribute('src');
      audioRef.current = null;
    }
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
    if (mountedRef.current) {
      setPlayingBlobName(null);
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      stop();
    };
  }, [stop]);

  /** Whether the given recording is the one currently playing. */
  const isPlaying = useCallback(
    (blobName: string | null | undefined) =>
      Boolean(blobName) && playingBlobName === blobName,
    [playingBlobName],
  );

  // Les echecs sont exposes dans `error` ET relances : l'ecran appelant doit
  // pouvoir afficher un message (avant, l'erreur etait avalee et le `.catch`
  // de l'ecran ne se declenchait jamais).
  const playBlob = useCallback(
    async (blobName: string | null | undefined) => {
      if (!blobName) {
        const missing = new Error('Aucune source audio exploitable');
        setError(missing);
        throw missing;
      }

      // Une seule lecture a la fois : la precedente s'arrete ici.
      stop();
      setIsLoading(true);
      setError(null);

      try {
        const blob = await audioApi.getPrivateAudioContent(blobName);
        if (!mountedRef.current) return;

        const objectUrl = URL.createObjectURL(blob);
        objectUrlRef.current = objectUrl;
        const audio = new Audio(objectUrl);
        audioRef.current = audio;

        const release = () => {
          if (failsafeRef.current !== null) {
            clearTimeout(failsafeRef.current);
            failsafeRef.current = null;
          }
          if (objectUrlRef.current === objectUrl) {
            URL.revokeObjectURL(objectUrl);
            objectUrlRef.current = null;
          }
          if (audioRef.current === audio) {
            audioRef.current = null;
          }
          if (mountedRef.current) {
            setPlayingBlobName(null);
          }
        };
        audio.addEventListener('ended', release, { once: true });
        audio.addEventListener('error', release, { once: true });
        // Garde-fou : si aucun evenement ne survient, on ne fuit pas l objet.
        // Il n'est efface qu'a la liberation, pas a la fin de `play`.
        failsafeRef.current = setTimeout(release, 60_000);

        try {
          await audio.play();
        } catch (playError) {
          // Lecture refusee (format, autoplay) : `ended` ne viendra jamais.
          release();
          throw playError;
        }
        if (mountedRef.current) {
          setPlayingBlobName(blobName);
        }
      } catch (playError) {
        if (mountedRef.current) setError(playError);
        throw playError;
      } finally {
        if (mountedRef.current) setIsLoading(false);
      }
    },
    [stop],
  );

  const play = useCallback(
    async (url: string | null | undefined) => {
      // Audio local (MediaRecorder) : lecture directe, aucun aller-retour API.
      if (typeof url === 'string' && /^(blob:|data:)/i.test(url.trim())) {
        try {
          await new Audio(url).play();
          setError(null);
        } catch (playError) {
          setError(playError);
          throw playError;
        }
        return;
      }
      await playBlob(blobNameFromAudioUrl(url));
    },
    [playBlob],
  );

  return { play, playBlob, stop, isPlaying, playingBlobName, isLoading, error };
}